import { useEffect, useState, useCallback } from 'react'
import { Box, VStack } from '@chakra-ui/react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { BrandBar } from '../../components/BrandBar'
import { TitleHeader } from '../../components/TitleHeader'
import { CardForm } from '../components/CardForm'
import { ConfirmButton } from '../components/ConfirmButton'
import { BackToSelectPaymentLink } from '../components/BackToSelectPaymentLink'
import { tokenizeCard } from '../services/gmoTokenizer'
import { creditTokenApiKeyAtom, creditTokenPublicKeyAtom } from '@!/atoms/paymentAtom'
import { useErrorToast } from '../hooks/useErrorToast'
import { getTokenizationErrorMessage } from '../services/newCardErrors'
import { useLogout } from '../../hooks/useLogout'

export const NewCard = ({ coinPlans }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { showError } = useErrorToast()
  const { logout, isLoading: isLogoutLoading } = useLogout()
  const apiKey = useAtomValue(creditTokenApiKeyAtom)
  const publicKey = useAtomValue(creditTokenPublicKeyAtom)

  const productId = searchParams.get('product_id')
  const coinPlan = coinPlans?.find((plan) => plan.product_id === productId)
  const isUpdate = location.state?.isUpdate || false // 前の画面から受け取ったisUpdateフラグ

  // CardFormから受け取るフォームの状態
  const [formState, setFormState] = useState({
    formData: {},
    isFormComplete: false,
  })

  const [isLoading, setIsLoading] = useState(false)

  // CardFormからフォームデータを受け取る
  const handleFormChange = useCallback((newFormState) => {
    setFormState(newFormState)
  }, [])

  // product_idが不正な場合はリダイレクト
  useEffect(() => {
    if (coinPlans && coinPlans.length > 0 && (!productId || !coinPlan)) {
      showError({
        title: 'コインが選択されていません',
        errorMessage: '',
        duration: 3000,
      })
      navigate('/')
    }
  }, [productId, coinPlan, coinPlans, navigate, showError])

  // 確認ボタン押下時の処理
  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      // GMOトークンを取得
      const token = await tokenizeCard(formState.formData, { apiKey, publicKey })

      // カード番号の下4桁を取得
      const cardNumber = formState.formData.cardNumber || ''
      const cardLast4 = cardNumber.slice(-4)

      // トークンとカード下4桁、isUpdateフラグをstateで渡して購入内容確認画面へ遷移
      navigate(`/confirm?product_id=${productId}`, {
        state: { gmoToken: token, cardLast4, isUpdate },
      })
    } catch (error) {
      console.error('Card tokenization error:', error)
      const errorMessage = getTokenizationErrorMessage(error.code)
      showError({
        title: 'エラー',
        errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // 戻るリンク押下時の処理
  const handleBack = () => {
    navigate(`/select_payment_method?product_id=${productId}`)
  }

  if (!coinPlan) return null

  return (
    <Box minH="100vh" bg="background.default" display="flex" justifyContent="center">
      <Box w="100%" maxW="500px">
        <VStack spacing={0} align="stretch">
          <BrandBar onLogout={logout} isLogoutLoading={isLogoutLoading} onLogoClick={() => navigate('/')} />
          <TitleHeader title="クレジット登録" />

          <Box py="16px" px="16px">
            <VStack spacing={6} align="stretch">
              <CardForm onFormChange={handleFormChange} />

              <ConfirmButton onClick={handleConfirm} isDisabled={!formState.isFormComplete} isLoading={isLoading} />

              <BackToSelectPaymentLink onClick={handleBack} />
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  )
}
